/**
 * Thomas Fogarty variant — UI overrides + extras for tenant `fogartywinery`.
 *
 * GUIDED FLOW (old addWelcomeMessageAndButtons):
 *   1. Visit Our Tasting Room                    → tasting experiences (QR-gated)
 *   2. Shop Our Wines                            → available wines
 *   3. Join Our Club                             → wine club memberships
 *   4. Winery Events                             → upcoming events (is_event)
 *   5. Weddings, Corporate & Private Events      → reserved events (is_event)
 *   6. Contact Our Team                          → __contact__
 *
 * REGISTERED EXTRAS:
 *   - WineCard — always registered (title / Appellation / Varietal / Alcohol +
 *     Add to cart; trash + qty select when in cart — old createWineListing)
 *   - MembershipCard — Join Now / no bullets
 *   - cart + checkout when shopify / show_cart
 *   - account greeting when show_user_account
 *   - contact-options / live-person-form
 */

import { on, addMessage, getState, setState } from "../../core/state.js";
import { getContact, getFeature, getUiFeature } from "../../core/configAccessors.js";
import { registerMessageKind } from "../../components/base/messages/index.js";
import { registry } from "../../core/registry.js";
import LRWineCard from "./components/WineCard.js";
import LRMembershipCard from "./components/MembershipCard.js";
import LRCheckoutForm from "./components/CheckoutForm.js";
import ContactOptions from "../../components/base/messages/ContactOptions.js";
import LivePersonForm from "../../components/base/messages/LivePersonForm.js";
import * as cartFeature from "../../features/optional/cart_c7.js";
import * as checkoutFeature from "../../features/optional/checkout_c7.js";
import * as accountFeature from "../../features/optional/account.js";
import LROrderReview from "./components/OrderReview.js";
import LROrderConfirmation from "./components/OrderConfirmation.js";
import LRLoginForm from "./components/LoginForm.js";


class ContactInfoCard {
  render(message) {
    const data = message?.data || {};
    const intro = data.intro || "Reach our team directly:";
    const phone = data.phone;
    const email = data.email;
    const address = data.address;
    const website = data.website;

    const row = document.createElement("div");
    row.className = "ws-bot-row";

    const avatar = document.createElement("div");
    avatar.className = "ws-avatar ws-bot-avatar";
    row.appendChild(avatar);

    const wrap = document.createElement("div");
    wrap.className = "ws-bubble-wrap";

    const bubble = document.createElement("div");
    bubble.className = "ws-bot-bubble";
    bubble.style.display = "flex";
    bubble.style.flexDirection = "column";
    bubble.style.gap = "6px";

    const introEl = document.createElement("div");
    introEl.textContent = intro;
    bubble.appendChild(introEl);

    if (phone) {
      const a = document.createElement("a");
      a.href = `tel:${phone.replace(/[^+0-9]/g, "")}`;
      a.textContent = `📞 ${phone}`;
      a.style.color = "inherit";
      a.style.fontWeight = "600";
      a.style.textDecoration = "underline";
      bubble.appendChild(a);
    }

    if (email) {
      const a = document.createElement("a");
      a.href = `mailto:${email}`;
      a.textContent = `✉️ ${email}`;
      a.style.color = "inherit";
      a.style.fontWeight = "600";
      a.style.textDecoration = "underline";
      bubble.appendChild(a);
    }

    if (address) {
      const span = document.createElement("div");
      span.textContent = `📍 ${address}`;
      bubble.appendChild(span);
    }

    if (website) {
      const a = document.createElement("a");
      a.href = website;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.textContent = `🌐 ${website.replace(/^https?:\/\//, "")}`;
      a.style.color = "inherit";
      a.style.fontWeight = "600";
      a.style.textDecoration = "underline";
      bubble.appendChild(a);
    }

    wrap.appendChild(bubble);
    row.appendChild(wrap);
    return row;
  }
}

// Parity with old showDefaultOptions (script.js 2522): after a contact follow-up,
// re-prompt with "Is there anything else…" + the guided buttons.
function postDefaultOptions() {
  addMessage({ role: "bot", kind: "text", data: { text: "Is there anything else I can help you with?" } });
  addMessage({ role: "bot", kind: "prompts", data: { items: getState().tenant.guided_flow_questions } });
}

// tel: href from a display phone (strip spaces/parens, keep +digits).
function telHref(phone) {
  return `tel:${String(phone || "").replace(/[^+0-9]/g, "")}`;
}

export async function register() {
  registerMessageKind("contact-info", ContactInfoCard);
  registerMessageKind("contact-options", ContactOptions);
  // registerMessageKind("live-person-form", LivePersonForm);

  // Override the base MembershipCard — the old Fogarty club card is simpler
  // (image, uppercase title, detailed_description, Learn more, Join Now) with no
  // type/meta/price. Runs after registerBaseCards (last wins). See the component.
  registry.registerComponent("MembershipCard", LRMembershipCard);

  // Parity: old Fogarty SHOP NOW (shopNowBanner) sent the wine-listing message,
  // it did NOT open a link. The tenant config only carries a link URL, so seed
  // the chat query here (tenant-scoped) — Banner.js prefers cta_query over the
  // URL, so the CTA fires "Show me all available wines" instead of navigating.
  const t = getState().tenant || {};
  if (t.theme && !t.theme.banner_cta_query) {
    setState({
      tenant: { ...t, theme: { ...t.theme, banner_cta_query: "Show me all available wines" } },
    });
  }

  // Always use Fogarty guided flow (labels + values from old widget). Tenant
  // config often has a different/generic list — force the Fogarty chips here.
  const cfg = getState().tenant || {};
  setState({ tenant: { ...cfg, guided_flow_questions: cfg.guided_flow_questions } });

  // Contact our team — match old showContactOptions: a bot question + the
  // connect options (Email Us / Call Us — no Text a Live Person on Commerce7
  // tenants, RedChirp isn't wired up for them). No login prompt here; old
  // shows that only on open (the account greeting, account.js).
  on("ws-prompt-select", ({ value }) => {
    if (value !== "__contact__") return;
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: "How would you like to connect with our team?" },
    });
    addMessage({
      role: "bot",
      kind: "contact-options",
      data: { email: getContact("email"), phone: getContact("phone"), text_live_person: false },
    });
  });

  // Text a Live Person → RedChirp contact form (old showContactForm): an intro
  // bot bubble, then the form (brand name feeds the legal text).
  // on("ws-contact-text", () => {
  //   addMessage({
  //     role: "bot",
  //     kind: "text",
  //     data: { text: "Enter your information and we will text you between the hours of 9AM to 5PM PST." },
  //   });
  //   const brand = (getState().tenant || {}).display_name || "the winery";
  //   addMessage({ role: "bot", kind: "live-person-form", data: { brand } });
  // });

  // Live-person form submitted. NOTE: the backend RedChirp endpoint does not
  // exist yet, so we show the old confirmation client-side (no transmission).
  on("ws-contact-submit", () => {
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: "We've received your information and we will text you between the hours of 9AM to 5PM PST." },
    });
    postDefaultOptions();
  });

  // Call Us → follow-up + re-prompt (old showContactNumber).
  on("ws-contact-call", () => {
    const phone = getContact("phone");
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: `You can call us at [${phone}](${telHref(phone)}). Our team is available during business hours.` },
    });
    postDefaultOptions();
  });

  // Email Us → follow-up + re-prompt (old showContactEmailMessage). The mailto
  // itself is opened by the ContactOptions link.
  on("ws-contact-email", () => {
    const phone = getContact("phone");
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: `We'll get back to you within 24–48 hours. For faster assistance, please call us at [${phone}](${telHref(phone)}). Our team is available during business hours.` },
    });
    postDefaultOptions();
  });

  // Always use Fogarty WineCard so Shop Our Wines title / Appellation /
  // Varietal / Alcohol + "Add to cart" match old createWineListing (even when
  // cart flags are off — otherwise the base rich card loads instead).
  registry.registerComponent("WineCard", LRWineCard);

  // Commerce7 cart — enabled when either show_cart or commerce7 is on in
  // tenant config.
  if (getUiFeature("show_cart", false) || getFeature("commerce7")) {
    cartFeature.init();
    // Full in-widget Commerce7 checkout flow (owns ws-checkout): checkout
    // options → ship/pickup form → order review → payment → confirmation.
    // checkout_c7.js is shared with Valle della Pace; the "reviewed" flow
    // opts into this tenant's own step sequence (see its header doc).
    checkoutFeature.init({ flow: "reviewed" });
    // Re-prompt with the guided buttons once the order is placed (parity with
    // the contact-flow follow-ups above).
    on("ws-order-confirmed", () => {
      postDefaultOptions();
    });
    // Lightning Rock ships C7 (no Shopify company/state/phone, CA-only
    // delivery, no Contact/email section) — override the base checkout-form
    // renderer, which otherwise renders for every C7 tenant.
    registerMessageKind("checkout-form", LRCheckoutForm);
    registerMessageKind("order-review", LROrderReview);
    registerMessageKind("order-confirmation", LROrderConfirmation);
  }

  // Account greeting / login prompt is gated on the account UI toggle ONLY —
  // NOT on commerce7. The logged-out prompt ("Looks like you're not logged
  // in…" + Login/Register) always showed on the old widget regardless.
  // Login renders in-chat (old showLoginForm/onLogin, Commerce7); Register
  // still links out. See account.js's commerce7 branch for the BE calls.
  if (getUiFeature("show_user_account", false)) {
    registerMessageKind("login-form", LRLoginForm);
    accountFeature.init();
  }
}
